import { readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { initializePropertyDefaults } from 'sh-www/utils/initialize-state-properties';
import { PROJECT_COMPLETION__SUB_VIEWS } from 'sh-www/constants/routes/project-completion';

export default Service.extend({
  /**
   * Register Services
   */
  session: service(),
  store: service(),
  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @type {Ember.A<Service>}     List of all supported services on SH
     */
    allServices: () => A(),

    /**
     * @type {Application}          The application we are completing.
     */
    application: null,

    /**
     * @type {Boolean}              This will be true when the user has selected the 'add to network' checkbox
     */
    isAddToNetworkSelected: true,

    /**
     * @type {Boolean}              True if we are showing the cost code error in the project-completion flow.
     */
    isCostCodeErrorShowing: false,

    /**
     * @type {Boolean}              True when we are submitting the project info form, first step in completion funnel
     */
    isSubmittingProjectInformationForm: false,

    /**
     * @type {Boolean}              True when we are submitting the private feedback form.
     */
    isSubmittingPrivateFeedback: false,

    /**
     * @type {String}               The private message for the storyhunter staff
     */
    notesForStoryhunterStaffMessage: '',

    /**
     * @type {String}               The message for the buyer's company team about the freelancer
     */
    notesForTeamMessage: '',

    /**
     * @type {Number}               The star count selected for the review
     */
    reviewStarCountSelected: 0,

    /**
     * @type {String}               The review text message supplied by the user.
     */
    reviewTextMessage: '',

    /**
     * @type {Ember.A<Service>}     List of all the selected services by the user completing the project.
     */
    selectedServices: () => A(),

    /**
     * This is essentially just the routes in the confirm completion funnel.  Ember 2.14 does not have an easy way to
     * access, so just using this for now.
     *
     * @type {String}               Of type PROJECT_COMPLETION__SUB_VIEWS.*
     */
    subView: PROJECT_COMPLETION__SUB_VIEWS.NONE,

    /** @type {ShPayment|ShInvoice} The test billing record. */
    testBillingRecord: undefined,

    /**
     * If user toggles to editing and cancels, the original billing record is stashed here so it can be
     * restored on cancel.
     *
     * @type {ShPayment|ShInvoice}
     */
    suspendedTestBillingRecord: undefined,

    /**
     * True if we are paying an advance, rather than actually completing the project.
     * @type {Boolean}
     */
    isAdvancePayment: false,

    /**
     * True if we are approving an existing advance, and not editing it.
     * @type {Boolean}
     */
    isApprovingAdvance: false,

    /**
     * True if we are editing an advance that needs agreement.
     * @type {Boolean}
     */
    isEditingAdvance: false,

    /** @type {Boolean} Whether we elect to expedite or not */
    expedite: false,

    /** @type {Boolean|null} Take the expedite fee responsibility from the other party */
    takeoverExpediteFee: null,

    /** @type {Number} */
    advanceBaseAmount: null,

    /** @type {Boolean} */
    advanceBaseSelected: false,

    /** @type {String} */
    advanceBaseDescription: '',

    /** @type {Object} */
    advanceProjectLineItemIds: () => A(),

    /** @type {ShInvoice|ShPayment} */
    approvingAdvanceBillingRecord: null,
  },

  /**
   * @type {Boolean}    True if we have any cost codes or story codes available to show.
   */
  hasAnyCostOrStoryCodes: computed(
  'currentUser.company.usesCostCodes',
  function() {
    return this.currentUser.company?.usesCostCodes;
  }),

  /**
   * @type {Boolean}                True after the buyer has entered all of their public feedback.
   */
  hasEnteredAllPublicFeedback: computed(
  'reviewStarCountSelected',
  function() {
    return this.get('reviewStarCountSelected');
  }),

  /**
   * @type {Boolean}
   */
  isBuyerViewing: readOnly('currentUser.isBuyer'),

  /**
   * @type {Boolean}
   */
  isSellerViewing: readOnly('currentUser.isSeller'),

  initializeDefaults() {
    initializePropertyDefaults(this);
  },

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    this.initializeDefaults();
  }
});
